import { useMediaQuery } from "@/hooks/useMediaQuery";

/**
 * Tailwind CSS default breakpoints
 * These match the standard Tailwind breakpoints used in your CSS
 */
export const BREAKPOINTS = {
  sm: "640px",
  md: "768px",
  lg: "1024px",
  xl: "1280px",
  "2xl": "1536px",
} as const;

export type Breakpoint = keyof typeof BREAKPOINTS;

export interface BreakpointState {
  /** Current active breakpoint (highest matching breakpoint) */
  current: Breakpoint | "base";
  /** True if viewport is at least 640px (sm breakpoint or higher) */
  isSm: boolean;
  /** True if viewport is at least 768px (md breakpoint or higher) */
  isMd: boolean;
  /** True if viewport is at least 1024px (lg breakpoint or higher) */
  isLg: boolean;
  /** True if viewport is at least 1280px (xl breakpoint or higher) */
  isXl: boolean;
  /** True if viewport is at least 1536px (2xl breakpoint or higher) */
  is2Xl: boolean;
  /** True if viewport is smaller than 768px (mobile) */
  isMobile: boolean;
  /** True if viewport is 768px or larger (tablet/desktop) */
  isDesktop: boolean;
}

/**
 * Public return type alias for useBreakpoint hook
 * Provides a stable public API for barrel re-exports
 */
export type UseBreakpointReturn = BreakpointState;

/**
 * Hook to detect which Tailwind breakpoint is currently active
 *
 * Returns an object with boolean flags for each breakpoint and convenience helpers.
 * All breakpoints use min-width queries to match Tailwind's mobile-first approach.
 *
 * @param defaultBreakpoint - The breakpoint to assume during SSR or before first render (defaults to "base" for mobile-first)
 * @returns An object containing breakpoint state information
 *
 * @example
 * ```tsx
 * const { isMobile, isDesktop, current } = useBreakpoint();
 *
 * // Conditional rendering based on screen size
 * return isMobile ? <MobileNav /> : <DesktopNav />;
 *
 * // Check specific breakpoint
 * if (isLg) {
 *   // Show expanded view on large screens
 * }
 *
 * // Use current breakpoint
 * console.log(`Current breakpoint: ${current}`); // "md", "lg", etc.
 * ```
 */
export function useBreakpoint(defaultBreakpoint: Breakpoint | "base" = "base"): BreakpointState {
  // Query each breakpoint using min-width (mobile-first approach, matching Tailwind)
  const isSm = useMediaQuery(`(min-width: ${BREAKPOINTS.sm})`, defaultBreakpoint !== "base");
  const isMd = useMediaQuery(`(min-width: ${BREAKPOINTS.md})`, ["md", "lg", "xl", "2xl"].includes(defaultBreakpoint));
  const isLg = useMediaQuery(`(min-width: ${BREAKPOINTS.lg})`, ["lg", "xl", "2xl"].includes(defaultBreakpoint));
  const isXl = useMediaQuery(`(min-width: ${BREAKPOINTS.xl})`, ["xl", "2xl"].includes(defaultBreakpoint));
  const is2Xl = useMediaQuery(`(min-width: ${BREAKPOINTS["2xl"]})`, defaultBreakpoint === "2xl");

  // Convenience helpers
  const isMobile = !isMd; // Less than md (768px)
  const isDesktop = isMd; // md or higher

  // Determine current breakpoint (highest matching breakpoint)
  let current: Breakpoint | "base" = "base";
  if (is2Xl) current = "2xl";
  else if (isXl) current = "xl";
  else if (isLg) current = "lg";
  else if (isMd) current = "md";
  else if (isSm) current = "sm";

  return {
    current,
    isSm,
    isMd,
    isLg,
    isXl,
    is2Xl,
    isMobile,
    isDesktop,
  };
}
